/* eslint-disable indent */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Card,
  Stack,
  Typography,
  styled,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConnectorInfo, keyValueApi } from 'types/types';
import { useUIContext } from 'modules/common/hooks/useUIContext';
import ErrorPage from 'modules/common/routes/ErrorPage/ErrorPage';
import { postRequest, getRequest } from 'utils/helper';
import { DagsHistoryTable } from './DagsHistoryTable';
import { MultipleCustomSettings } from './DagCustomSettings/MultipleCustomSettings';
import { EditWorkflowDialog } from './EditWorkflowDialog';

const DAG_STATUS = {
  YES: 'Yes',
  NO: 'No'
} as const;

const DAG_SWITCH = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled'
} as const;

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 600000;

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 2, 1, 2)
  },
  dagDescription: {
    color: theme.palette.text.secondary,
    paddingBottom: '10px',
    fontWeight: theme.typography.fontWeightBold
  },
  head: {
    color: theme.palette.text.secondary,
    textOrientation: 'sideways',
    writingMode: 'vertical-lr'
  },
  stack: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  repliconLogoNew: {
    width: theme.spacing(6.2),
    paddingLeft: theme.spacing(1)
  },
  arrowIcon: {
    fontSize: 40,
    color: theme.palette.text.secondary
  },
  divButton: {
    paddingRight: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column'
  },
  card: { margin: theme.spacing(2, 0) },
  settingButton: {
    color: 'grey',
    marginLeft: '-3px',
    marginTop: theme.spacing(1)
  },
  editButton: { marginLeft: theme.spacing(2) },
  emptyDiv: { height: theme.spacing(2) }
}));

const DagSwitch = styled(Switch)(({ theme }) => ({
  width: 30,
  height: 16,
  padding: 0,
  margin: 8,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)'
    }
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#65C466'
      }
    }
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200
    })
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,.35)'
        : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box'
  }
}));

type ConnectorSyncProps = {
  info: ConnectorInfo;
  dagId: string;
  dagDescription: string;
  arrowIcon: boolean;
  dagSwitchStatus: string;
  initialCustomSetting: any;
  mappedWorkflow?: string | null;
  dagSetting: { dagId: string; status: string } | null;
  setDagSetting: React.Dispatch<
    React.SetStateAction<{ dagId: string; status: string } | null>
  >;
  pendingEnable?: boolean;
  customSettingsSaved?: boolean;
};

export const ConnectorSyncPage: React.FC<ConnectorSyncProps> = ({
  info,
  dagDescription,
  dagId,
  arrowIcon,
  dagSwitchStatus,
  initialCustomSetting,
  mappedWorkflow,
  dagSetting,
  setDagSetting,
  pendingEnable = false,
  customSettingsSaved = true
}: ConnectorSyncProps) => {
  const dag = info.dagsSection.filter(
    (dagSection) => dagSection.dagId === dagId
  )[0];

  const { formatMessage } = useIntl();
  const [dagSwitch, setDagSwitch] = useState(dagSwitchStatus);
  const [dagHistorySaved, setDagHistorySaved] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dagSettings, setDagSettings] = useState(initialCustomSetting);
  const [isPendingEnable, setIsPendingEnable] = useState(pendingEnable);
  const [isCustomSettingsSaved, setIsCustomSettingsSaved] = useState(
    customSettingsSaved
  );
  const [expanded, setExpanded] = React.useState(
    Array.isArray(dag.customSettings) ? false : true
  );
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isPendingEnable) return;

    const stopPolling = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const applyMatch = (match?: { enabled?: string }) => {
      if (!match) return;
      setDagSwitch(
        match.enabled === DAG_STATUS.YES
          ? DAG_SWITCH.ENABLED
          : DAG_SWITCH.DISABLED
      );
    };

    const fetchDagMatch = async () => {
      try {
        const res = await getRequest(
          '/integration-settings-api/dag-configuration',
          {
            connectorName: info.shortName,
            companyKey: info.companyKey,
            key: keyValueApi.dagSettings
          },
          false
        );
        return res.data?.find?.((d: any) => d.workflowId === dagId);
      } catch (_) {
        return undefined;
      }
    };

    pollRef.current = setInterval(async () => {
      const match = await fetchDagMatch();
      if (match && !match.pendingEnable) {
        setDagHistorySaved((prev) => !prev);
        stopPolling();
        setIsPendingEnable(false);
        applyMatch(match);
      }
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(async () => {
      const match = await fetchDagMatch();
      stopPolling();
      setIsPendingEnable(false);
      applyMatch(match);
    }, POLL_TIMEOUT_MS);

    return stopPolling;
  }, [isPendingEnable, info.shortName, info.companyKey, dagId]);

  const { genericKeyValues } = useUIContext() || {};
  const handleExpandClick = useCallback(() => {
    if (Array.isArray(dag.customSettings)) {
      setExpanded(!expanded);
    }
  }, [expanded, dag.customSettings]);
  const classes = useStyles();

  const handleEnableClick = useCallback(
    ({ status, value }: { status: string; value: string }) => {
      setDagSwitch(value);
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        dagId: dagId,
        status: status,
        key: keyValueApi.dagSettings
      };
      postRequest('/integration-settings-api/dag-configuration', body, false)
        .then((res) => {
          if (res.data) {
            setDagSetting(res.data);
          }
        })
        .catch((error: any) => {
          if (error.response) {
            setShowErrorPage(true);
            setDagSwitch(dagSwitch);
          }
        });
    },
    [info.shortName, info.companyKey, dagId, dagSwitch, setDagSetting]
  );

  const getExpandButtonText = useCallback((connectorName) => {
    const messageId =
      connectorName === 'replicon'
        ? 'repliconReportConnector.filters'
        : 'dagCustomSettings.settings';
    return <FormattedMessage id={messageId} />;
  }, []);

  const handleClick = useCallback(() => {
    if (dagSwitch === DAG_SWITCH.ENABLED) {
      handleEnableClick({
        status: DAG_STATUS.NO,
        value: DAG_SWITCH.DISABLED
      });
    } else {
      handleEnableClick({
        status: DAG_STATUS.YES,
        value: DAG_SWITCH.ENABLED
      });
    }
  }, [dagSwitch, handleEnableClick]);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <Card className={classes.card} elevation={1} data-testid="all-components">
      <div className={classes.container}>
        <Stack className={classes.stack} data-testid="stack-description">
          <Typography
            whiteSpace="nowrap"
            className={classes.dagDescription}
            data-testid={'header-description'}
          >
            {dagDescription}
          </Typography>
          {info.installed &&
            info.shortName != 'replicon' &&
            isCustomSettingsSaved && (
              <div className={classes.divButton} style={{ marginTop: '0px' }}>
                <Stack className={classes.stack}>
                  <Tooltip
                    title={
                      isPendingEnable
                        ? formatMessage({
                            id: 'vantagepointInstallDialog.pendingEnableTooltip'
                          })
                        : ''
                    }
                    placement="top"
                  >
                    <span>
                      <FormControlLabel
                        sx={{
                          color:
                            dagSwitch === DAG_SWITCH.DISABLED || isPendingEnable
                              ? 'text.disabled'
                              : 'text.primary'
                        }}
                        control={
                          <DagSwitch
                            value={dagSetting ? dagSetting : ''}
                            checked={
                              !isPendingEnable &&
                              dagSwitch === DAG_SWITCH.ENABLED
                            }
                            onClick={handleClick}
                            disabled={isPendingEnable}
                          />
                        }
                        label={
                          isPendingEnable
                            ? formatMessage({
                                id:
                                  'vantagepointInstallDialog.pendingEnableLabel'
                              })
                            : dagSwitch
                        }
                        labelPlacement="start"
                        data-testid="enable/disable button"
                      />
                    </span>
                  </Tooltip>
                  {genericKeyValues?.editWorkflowAccess && (
                    <Tooltip title="Map to a custom DAG">
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.editButton}
                        onClick={() => setEditDialogOpen(true)}
                        data-testid="edit-button"
                      >
                        <FormattedMessage id={'editWorkflow.edit'} />
                      </Button>
                    </Tooltip>
                  )}
                  <EditWorkflowDialog
                    open={editDialogOpen}
                    info={info}
                    dagId={dagId}
                    mappedWorkflow={mappedWorkflow}
                    setEditDialogOpen={setEditDialogOpen}
                  />
                </Stack>
              </div>
            )}
        </Stack>
        <Stack className={classes.stack} data-testid="stack-context">
          <div>
            {info.shortName != 'replicon' && (
              <Stack className={classes.stack} data-testid="action-trigger">
                <Typography
                  className={classes.head}
                  data-testid={'check-trigger'}
                >
                  <FormattedMessage id={'dagHeading.trigger'} />
                </Typography>
                <img
                  data-testid="trigger-logo"
                  src={
                    arrowIcon
                      ? `connectorDescriptionPages/${info?.shortName}/${info?.shortName}.recipe.svg`
                      : 'circleRepliconLogoNew.svg'
                  }
                  className={classes.repliconLogoNew}
                />
                <ArrowRightIcon
                  className={classes.arrowIcon}
                  data-testid="RightIcon"
                />
                <Typography
                  className={classes.head}
                  data-testid={'check-actions'}
                >
                  <FormattedMessage id={'dagHeading.actions'} />
                </Typography>
                <img
                  data-testid="action-logo"
                  src={
                    arrowIcon
                      ? 'circleRepliconLogoNew.svg'
                      : `connectorDescriptionPages/${info?.shortName}/${info?.shortName}.recipe.svg`
                  }
                  className={classes.repliconLogoNew}
                />
              </Stack>
            )}
            <Stack className={classes.stack}>
              {Array.isArray(dag.customSettings) &&
                dag.customSettings.length != 0 &&
                info.installed && (
                  <Button
                    variant="text"
                    onClick={handleExpandClick}
                    className={classes.settingButton}
                    data-testid="setting-button"
                  >
                    {getExpandButtonText(info.shortName)}
                  </Button>
                )}
            </Stack>
          </div>
        </Stack>
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          data-testid="collapse-actions"
        >
          {dag.customSettings.length && (
            <MultipleCustomSettings
              info={info}
              dagId={dagId}
              dag={dag}
              setDagSettings={setDagSettings}
              initialCustomSetting={dagSettings}
              setDagHistorySaved={setDagHistorySaved}
              dagSwitch={dagSwitch}
              isPendingEnable={isPendingEnable}
              setIsPendingEnable={setIsPendingEnable}
              setIsCustomSettingsSaved={setIsCustomSettingsSaved}
            />
          )}
        </Collapse>
        {!info.installed ? (
          <div className={classes.emptyDiv}></div>
        ) : (
          <DagsHistoryTable
            companyKey={info.companyKey}
            identifier={`${info.shortName}_${dagId}`}
            dagHistorySaved={dagHistorySaved}
          />
        )}
      </div>
    </Card>
  );
};
